import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { KyowaKirinWordmark } from './KyowaKirinWordmark';
import { conceptAmbition, conceptSections, opportunities, opportunitiesClosing } from '../../data/proposal';
type ConceptModalProps = {
  open: boolean;
  /** Scroll straight to the "one possibility" list when opened from the opportunities link. */
  focusOpportunities?: boolean;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function ConceptModal({
  open,
  focusOpportunities = false,
  onClose
}: ConceptModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const opportunitiesRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    const t = focusOpportunities ? window.setTimeout(() => opportunitiesRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    }), 260) : undefined;
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (t) window.clearTimeout(t);
    };
  }, [open, focusOpportunities, onClose]);
  return <AnimatePresence data-id="element-22" data-mp="2yathjf9ae9gj">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-23" data-mp="2y9s639oczkya">
          <button type="button" aria-label="Close concept" onClick={onClose} className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" data-id="element-24" data-mp="2td1ouj0tijba" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="concept-title" className="relative flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_40px_100px_-30px_rgba(14,27,51,0.5)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} data-id="element-25" data-mp="2kbu310fhnay3">
            <header className="flex items-center justify-between gap-4 border-b border-navy/10 px-6 py-4 md:px-10" data-id="element-26" data-mp="18u7qhhh24iz9">
              <div className="flex items-center gap-5" data-id="element-27" data-mp="14smgq4ztkkta">
                <KyowaKirinWordmark data-id="element-28" data-mp="14qry4xc0t4sd" />
                <span className="hidden h-7 w-px bg-navy/10 sm:block" aria-hidden="true" data-id="element-29" data-mp="zt9sbftav5ol" />
                <p id="concept-title" className="hidden font-mono text-[11px] tracking-[0.24em] text-graphite/70 sm:block" data-id="element-30" data-mp="uv80o6ht5y1i">
                  KYOWA KIRIN × SNAKKER™ · THE CONCEPT
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2 text-navy/60 transition-colors duration-150 hover:bg-paper hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy" data-id="element-31" data-mp="3fknvrdo288wy">
                <XIcon size={20} strokeWidth={1.75} data-id="element-32" data-mp="3k5wnax99ns1v" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10" data-id="element-33" data-mp="3o7i8r6zv9eno">
              <div className="grid gap-x-12 gap-y-8 md:grid-cols-3" data-id="element-34" data-mp="3o9kxlnpa91qt">
                {conceptSections.map(s => <section key={s.label} data-id="element-35" data-mp="20a7ogvzbtxg">
                    <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-teal" data-id="element-36" data-mp="21bl2s0gwsic">{s.label}</h2>
                    <p className="mt-3 text-[16px] leading-relaxed text-ink" data-id="element-37" data-mp="6yv03u4zp9bc">{s.body}</p>
                  </section>)}
              </div>

              <section className="mt-10 border-l-2 border-navy pl-5" data-id="element-38" data-mp="28q82abfkp33p">
                <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-teal" data-id="element-39" data-mp="28skq3580i7oo">{conceptAmbition.label}</h2>
                <p className="mt-3 text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] text-ink md:text-[32px]" data-id="element-40" data-mp="3u9a6vv9xwyc1">
                  <span className="text-graphite" data-id="element-41" data-mp="c1ufjl76npsr">{conceptAmbition.lead}</span>
                  <br data-id="element-42" data-mp="73swh09rbqha" />
                  {conceptAmbition.emphasis}
                </p>
              </section>

              <section ref={opportunitiesRef} aria-labelledby="opportunities-title" className="mt-10 scroll-mt-6 border-t border-ink/10 pt-7" data-id="element-43" data-mp="3fkw1pdp1wxsb">
                <h2 id="opportunities-title" className="text-[14px] font-medium text-graphite" data-id="element-44" data-mp="3bg6a411fe5d7">
                  By the way, this is just one possibility.
                </h2>
                <ul className="mt-3 divide-y divide-ink/[0.07]" data-id="element-45" data-mp="3khe78fs7h05p">
                  {opportunities.map(o => <li key={o.title} className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-baseline sm:gap-3" data-id="element-46" data-mp="2wra223zdcscl">
                      <span className="text-[14.5px] font-medium text-ink" data-id="element-47" data-mp="17c93lq9w8aqj">{o.title}</span>
                      <span className="hidden text-graphite/50 sm:inline" aria-hidden="true" data-id="element-48" data-mp="17aefk33bs2i8">
                        —
                      </span>
                      <span className="text-[14px] text-graphite" data-id="element-49" data-mp="yclzuxtoysub">{o.detail}</span>
                    </li>)}
                </ul>
                <p className="mt-5 text-[14px] leading-relaxed text-ink" data-id="element-50" data-mp="1389ptffzzq0v">{opportunitiesClosing}</p>
              </section>
            </div>

            <footer className="border-t border-navy/10 bg-paper px-6 py-4 md:px-10" data-id="element-51" data-mp="1v23h8a21e3bu">
              <p className="text-[11px] leading-relaxed text-graphite/80" data-id="element-52" data-mp="1h2k8bf1einyh">
                The idea, concept, Snak format and creative execution presented here are the intellectual property of Snakker™ and
                are shared in confidence for discussion purposes only. They may not be reproduced, adapted or used without prior
                written consent. Illustrative concept, not reviewed by Kyowa Kirin medical, legal or regulatory. © Snakker™ 2026 ·
                Confidential &amp; proprietary.
              </p>
            </footer>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}